import { defineStore } from "pinia";
import { useTalkRoomStore } from "./TalkRoomStore";
import { useContentsStore } from "./ContentsStore";
import { useLibraryStore } from "./LibraryStore";
import { PlanInfo } from "~/entities/PlanInfo";
import { PlanReviews } from "~/entities/PlanReviews";
import { PlanDocument } from "~/entities/PlanDocument";
import { PlanReviewInfo } from "~/entities/PlanReviewInfo";
import { Plans } from "~/entities/Plans";
import { type IPlanRepository } from "~/interfaces/IPlanRepository";
import { PlanRepository } from "~/repositories/PlanRepository";
import { useLoginStore } from "~/stores/LoginStore";
import { usePlanEditStore } from "~/stores/PlanEditStore";
import { ContractMemberResponse } from "~/entities/ContractMemberResponse";
import { ContractStatisticResponse } from "~/entities/ContractStatisticResponse";
import { PlanReviewApplicableResponse } from "~/entities/PlanReviewApplicableResponse";
import { PlanReviewRequest } from "~/entities/PlanReviewRequest";
import type { ContractMember } from "~/entities/ContractMember";
import type { MemberDetailResponse } from "~/entities/MemberDetailResponse";
import { type CONTENT_SORT_TYPE, type PLAN_TYPE } from "~/consts/contentsConfig";
import type { UserTagType } from "~/entities/UserTagType";

export const usePlanStore = defineStore("PlanStore", () => {
  const plan = ref<PlanInfo>();
  const planReviews = ref<PlanReviews>();
  const myReview = ref<PlanReviewInfo>();
  const planSummaryReviews = ref<PlanReviews>();
  const planDocuments = ref<PlanDocument[]>([]);
  const planOwners = ref<{ userId: string; userName: string }[]>([]);

  const plans = ref<Plans>();
  const ownerPlans = ref<Plans>(); // オーナー指定で取得した講座
  const myOwnerPlans = ref<Plans>(); // 自分の講座
  const contractedPlans = ref<Plans>(); // 契約中の講座
  const planReviewApplicable = ref<PlanReviewApplicableResponse>();
  const contractMemberResponse = ref<ContractMemberResponse>();
  const contractStatisticResponse = ref<ContractStatisticResponse>();

  const talkRoomStore = useTalkRoomStore();
  const contentsStore = useContentsStore();

  const libraryStore = useLibraryStore();

  const filterMemberConditions = ref<{
    userTagConditions: UserTagType[];
  }>({
    userTagConditions: [],
  });

  const loading = ref(false);
  // リスト系のloading
  const loadingList = ref(false);
  // postReviewは別物なのでloadingフラグを分ける
  const loadingPostReview = ref(false);
  // メンバーは別にする
  const loadingMembers = ref(false);
  // 統計のloading
  const loadingStatistics = ref(false);

  // オーナーのloading
  const loadingOwner = ref(false);

  const loginStore = useLoginStore();
  const planEditStore = usePlanEditStore();

  const repository: IPlanRepository = new PlanRepository();

  const setFilterMemberTagConditions = (userTagConditions: UserTagType[]) => {
    filterMemberConditions.value = {
      ...filterMemberConditions.value,
      userTagConditions,
    };
  };

  // store内のplanが指定のplanIdと別だった場合はplanをリセットする
  const resetPlan = (planId: string | undefined) => {
    if (plan.value?.id !== planId) {
      plan.value = undefined;
      filterMemberConditions.value = {
        userTagConditions: [],
      };
      planEditStore.reset();
    }
  };

  const fetch = async (
    page: number,
    pageSize: number,
    category?: string | undefined,
    isAdvice?: boolean | undefined,
    ownerUserId?: string | undefined,
    isContracted?: boolean | undefined,
    isSubscription?: boolean | undefined,
    sort?: CONTENT_SORT_TYPE,
    planType?: PLAN_TYPE,
  ) => {
    loadingList.value = true;
    try {
      plans.value = await repository.fetch(page, pageSize, {
        category,
        isAdvice,
        ownerUserId,
        isContracted,
        isSubscription,
        sort,
        planType,
      });
    } finally {
      loadingList.value = false;
    }
  };

  /**
   * オーナー指定で取得し、ownerPlansに値を入れる
   * @param page
   * @param pageSize
   * @param category
   * @param ownerUserId
   * @param isContracted
   * @param isSubscription
   */
  const fetchOwnerPlan = async (
    page: number,
    pageSize: number,
    category: string | undefined,
    isAdvice: boolean | undefined,
    ownerUserId: string | undefined,
    isContracted: boolean | undefined,
    isSubscription: boolean | undefined,
  ) => {
    loading.value = true;
    try {
      ownerPlans.value = undefined;
      ownerPlans.value = await repository.fetch(page, pageSize, {
        category,
        isAdvice,
        ownerUserId,
        isContracted,
        isSubscription,
      });
    } finally {
      loading.value = false;
    }
  };
  const fetchMyOwnerPlan = async (isOnlyPlanInfo?: boolean) => {
    if (loginStore.isOwner()) {
      loadingList.value = true;
      try {
        myOwnerPlans.value = await repository.fetch(1, 1000, {
          ownerUserId: loginStore.loginInfo.userId,
          isOnlyPlanInfo,
        });
      } finally {
        loadingList.value = false;
      }
    }
  };

  const fetchContractedPlan = async () => {
    loadingList.value = true;
    try {
      contractedPlans.value = await repository.fetch(1, 1000, {
        isContracted: true,
      });
    } finally {
      loadingList.value = false;
    }
  };

  const fetchOne = async (planId: string, checkUnreadTalk = false, checkUnreadContent = false) => {
    loading.value = true;
    try {
      plan.value = await getOne(planId);
      if (plan.value && checkUnreadTalk) {
        // トーク未読チェックは重く表示ブロックしてしまうので、非同期で走らせ後ほどフラグ設定
        talkRoomStore
          .hasUnreadTalk(planId)
          .then(hasUnread => {
            // 非同期処理中にplanId が変わってしまった場合に備えてガード
            if (plan.value?.id === planId) {
              plan.value.hasUnreadTalk = hasUnread;
            }
          })
          .catch(error => {
            consoleError(`Error checking unread talks for planId ${planId}:`, error);
          });
      }
      if (plan.value && checkUnreadContent) {
        // トークのチェックが入る場合には負荷軽減のため少し待つ。ciでエラーになる
        if (checkUnreadTalk) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        // 未読系の処理は非同期で行う。オーナーの場合は非公開コンテンツも取得する
        contentsStore.getPlanContentsUnreadAll(planId, !plan.value.isOwner);
        // ライブラリの未読情報を取得
        libraryStore.getPlanLibraryUnread(planId);
      }
    } finally {
      loading.value = false;
    }
  };

  // 講座情報を取得する。loadingフラグは立てない。plan.valueを更新しない。
  const getOne = async (planId: string) => {
    return await repository.fetchOne(planId);
  };

  const fetchPlanReviews = async (
    page: number,
    pageSize: number,
    planId: string | undefined,
    userId?: string | undefined,
  ) => {
    loading.value = true;
    try {
      planReviews.value = await repository.fetchPlanReviews(page, pageSize, planId, userId);
    } finally {
      loading.value = false;
    }
  };

  const fetchMyReview = async (planId: string | undefined, userId: string | undefined) => {
    loading.value = true;
    try {
      myReview.value = undefined;
      const response = await repository.fetchPlanReviews(1, 1, planId, userId);
      if (response.reviews?.length) {
        myReview.value = response.reviews[0];
      }
    } finally {
      loading.value = false;
    }
  };

  // 講座サマリー向けのデータ取得
  const fetchPlanSummaryReviews = async (page: number, pageSize: number, planId: string | undefined) => {
    loading.value = true;
    try {
      planSummaryReviews.value = await repository.fetchPlanReviews(page, pageSize, planId);
    } finally {
      loading.value = false;
    }
  };

  const fetchPlanDocuments = async (planId: string) => {
    loading.value = true;
    try {
      planDocuments.value = await repository.fetchDocuments(planId);
    } finally {
      loading.value = false;
    }
  };

  const fetchMembers = async (page: number, pageSize: number, options?: Record<string, any>) => {
    loadingMembers.value = true;
    try {
      contractMemberResponse.value = await repository.fetchMembers(page, pageSize, options);
    } finally {
      loadingMembers.value = false;
    }
  };

  const fetchMember = async (planId: string, userId: string): Promise<ContractMember> => {
    const res = await repository.fetchMembers(1, 1, { user_id: userId, plan_id: planId });
    return res.contractMembers[0];
  };

  const fetchMemberDetail = async (memberId: string): Promise<MemberDetailResponse> => {
    return await repository.fetchMemberDetail(memberId);
  };

  const fetchStatistics = async (planPriceId?: number[], options?: Record<string, any>) => {
    if (!contractStatisticResponse.value) {
      loadingStatistics.value = true;
    }
    try {
      contractStatisticResponse.value = await repository.fetchStatistics(planPriceId, options);
    } finally {
      loadingStatistics.value = false;
    }
  };

  const fetchReviewApplicable = async (planId: string) => {
    loading.value = true;
    planReviewApplicable.value = undefined;
    try {
      planReviewApplicable.value = await repository.fetchReviewApplicable(planId);
    } finally {
      loading.value = false;
    }
  };

  const postReview = async (planId: string, planReviewRequest: PlanReviewRequest) => {
    loadingPostReview.value = true;
    try {
      await repository.postReview(planId, planReviewRequest);
    } finally {
      loadingPostReview.value = false;
    }
  };

  const putReview = async (reviewId: string, planReviewRequest: PlanReviewRequest) => {
    loadingPostReview.value = true;
    try {
      await repository.putReview(reviewId, planReviewRequest);
    } finally {
      loadingPostReview.value = false;
    }
  };

  const deleteReview = async (reviewId: string) => {
    loadingPostReview.value = true;
    try {
      await repository.deleteReview(reviewId);
    } finally {
      loadingPostReview.value = false;
    }
  };

  const updateReviewPrivate = async (reviewId: string, isPrivate: boolean) => {
    loadingPostReview.value = true;
    try {
      await repository.updateReviewPrivate(reviewId, isPrivate);
    } finally {
      loadingPostReview.value = false;
    }
  };

  // レビュー可能かを返す
  // 汎用的に使えるようにplan、planReviewApplicableを引数にしている
  const isReviewable = (
    plan: PlanInfo | undefined,
    planReviewApplicable: PlanReviewApplicableResponse | undefined,
  ): boolean => {
    if (!plan || !planReviewApplicable) {
      // データ取得前は非表示
      return false;
    }

    if (!isContractPlan(plan)) {
      // 入会していない場合は非表示
      return false;
    }

    if (!plan.isAvailableReview) {
      // 非表示の場合は非表示
      return false;
    }

    // レビュー可能か
    if (planReviewApplicable.isReviewAvailable === false) {
      return false;
    }
    return true;
  };

  const fetchOwners = async (planId: string) => {
    loadingOwner.value = true;
    try {
      planOwners.value = await repository.fetchOwners(planId);
      loadingOwner.value = false;
    } catch (error: any) {
      loadingOwner.value = false;
      console.error(error);
    }
  };

  return {
    plan,
    planReviews,
    myReview,
    planSummaryReviews,
    planDocuments,
    plans,
    ownerPlans,
    myOwnerPlans,
    contractedPlans,
    contractMemberResponse,
    contractStatisticResponse,
    planReviewApplicable,
    planOwners,
    loading,
    loadingList,
    loadingPostReview,
    loadingMembers,
    loadingStatistics,
    loadingOwner,
    filterMemberConditions,
    setFilterMemberTagConditions,
    resetPlan,
    fetchOne,
    getOne,
    fetch,
    fetchOwnerPlan,
    fetchMyOwnerPlan,
    fetchContractedPlan,
    fetchPlanReviews,
    fetchMyReview,
    fetchPlanSummaryReviews,
    fetchPlanDocuments,
    fetchMembers,
    fetchMember,
    fetchStatistics,
    fetchReviewApplicable,
    postReview,
    putReview,
    deleteReview,
    updateReviewPrivate,
    isReviewable,
    fetchOwners,
    fetchMemberDetail,
  };
});
